import React, { useState, useEffect, useRef } from 'react';
import { Copy, RefreshCw, Link2, Users, CheckCircle2, Loader2, Download, Printer, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { apiService } from '@/lib/api';

export default function InviteContractors({ userId, companyName, isOpen, onClose }) {
  const [signupCode, setSignupCode] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const qrRef = useRef(null);
  
  // Email invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Fetch signup code on mount
  useEffect(() => {
    if (isOpen && userId) {
      fetchSignupCode();
    }
  }, [isOpen, userId]);

  const fetchSignupCode = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getContractorSignupCode(userId);
      setSignupCode(response.data.signup_code);
      setSignupUrl(response.data.signup_url);
    } catch (error) {
      console.error('Failed to fetch signup code:', error);
      toast.error('Failed to load invite link');
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateCode = async () => {
    try {
      setIsRegenerating(true);
      const response = await apiService.regenerateContractorSignupCode(userId);
      setSignupCode(response.data.signup_code);
      setSignupUrl(response.data.signup_url);
      toast.success('New invite link generated');
    } catch (error) {
      console.error('Failed to regenerate signup code:', error);
      toast.error('Failed to generate new link');
    } finally {
      setIsRegenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(signupUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const downloadQRCodePDF = async () => {
    setIsDownloading(true);
    try {
      // Get the QR code SVG element
      const svgElement = qrRef.current?.querySelector('svg');
      if (!svgElement) {
        toast.error('QR code not found');
        return;
      }

      // Convert SVG to canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      });

      // Create a larger canvas for better print quality
      const scale = 3;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);

      // Create a printable HTML document
      const qrImageData = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        toast.error('Please allow popups to download the QR code');
        return;
      }

      const displayName = companyName || 'Our Company';
      // Escape HTML so a stray <, >, or quote in the user's own company name
      // can't break the print layout (or be a vector if data is ever federated).
      const escape = (s) => String(s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
      const safeDisplayName = escape(displayName);
      const safeSignupUrl = escape(signupUrl);
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Contractor QR Code - ${safeDisplayName}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 40px;
              box-sizing: border-box;
              background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
            }
            .card {
              background: white;
              border-radius: 24px;
              padding: 48px;
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
              text-align: center;
              max-width: 500px;
              width: 100%;
            }
            .logo {
              width: 64px;
              height: 64px;
              background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
              border-radius: 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 24px;
              color: white;
              font-size: 28px;
              font-weight: bold;
            }
            .company-name {
              font-size: 24px;
              font-weight: 700;
              color: #1e293b;
              margin-bottom: 8px;
            }
            .subtitle {
              font-size: 16px;
              color: #64748b;
              margin-bottom: 32px;
            }
            .qr-container {
              background: #f8fafc;
              border: 2px solid #e2e8f0;
              border-radius: 16px;
              padding: 24px;
              margin-bottom: 24px;
            }
            .qr-code {
              max-width: 280px;
              width: 100%;
              height: auto;
            }
            .scan-text {
              font-size: 18px;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 8px;
            }
            .instructions {
              font-size: 14px;
              color: #64748b;
              line-height: 1.6;
            }
            .url {
              font-size: 12px;
              color: #94a3b8;
              word-break: break-all;
              margin-top: 16px;
              padding: 12px;
              background: #f1f5f9;
              border-radius: 8px;
            }
            .footer {
              margin-top: 24px;
              font-size: 12px;
              color: #94a3b8;
            }
            .print-btn {
              margin-top: 24px;
              padding: 12px 32px;
              background: #3b82f6;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
            }
            .print-btn:hover {
              background: #2563eb;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="logo">D</div>
            <div class="company-name">${safeDisplayName}</div>
            <div class="subtitle">Join Our Contractor Network</div>
            
            <div class="qr-container">
              <img src="${qrImageData}" alt="QR Code" class="qr-code" />
            </div>
            
            <div class="scan-text">Scan to Register</div>
            <div class="instructions">
              Scan this QR code with your phone to register as a contractor.<br/>
              You'll need to provide your company info and W-9.
            </div>
            
            <div class="url">${safeSignupUrl}</div>
            
            <div class="footer">Powered by DrawReady</div>
            
            <button class="print-btn no-print" onclick="window.print()">
              Print This Sign
            </button>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      toast.success('QR code opened in new tab - ready to print!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate printable QR code');
    } finally {
      setIsDownloading(false);
    }
  };

  const sendEmailInvite = async () => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!inviteEmail || !emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSendingEmail(true);
      await apiService.sendContractorInviteEmail(userId, {
        email: inviteEmail,
        message: inviteMessage,
        signup_url: signupUrl
      });
      
      setEmailSent(true);
      toast.success(`Invite sent to ${inviteEmail}!`);
      
      // Reset form after short delay
      setTimeout(() => {
        setInviteEmail('');
        setInviteMessage('');
        setEmailSent(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to send invite:', error);
      toast.error('Failed to send invite email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Users className="w-5 h-5 text-blue-600" />
            Invite Contractors
          </DialogTitle>
          <DialogDescription>
            Share this QR code or link with contractors to let them self-register
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="share" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="share" className="gap-2">
                <Link2 className="w-4 h-4" />
                Share Link
              </TabsTrigger>
              <TabsTrigger value="email" className="gap-2">
                <Mail className="w-4 h-4" />
                Send Email
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="share" className="space-y-6">
              {/* QR Code Display */}
              <div className="flex flex-col items-center p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                <div ref={qrRef} className="bg-white p-4 rounded-xl shadow-sm mb-4">
                  <QRCodeSVG 
                    value={signupUrl} 
                    size={180}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#1e293b"
                  />
                </div>
                <p className="text-sm text-slate-500 text-center mb-3">
                  Scan to register as a contractor
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={downloadQRCodePDF}
                  disabled={isDownloading}
                  className="gap-2"
                  data-testid="download-qr-btn"
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Printer className="w-4 h-4" />
                  )}
                  Download Printable Sign
                </Button>
              </div>

              {/* Link & Copy Section */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Invite Link
                </label>
                <div className="flex gap-2">
                  <Input 
                    value={signupUrl} 
                    readOnly 
                    className="flex-1 bg-slate-50 font-mono text-sm"
                    data-testid="invite-link-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={copyToClipboard}
                    className={copied ? 'bg-green-50 border-green-200 text-green-700' : ''}
                    data-testid="copy-link-btn"
                  >
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <Card className="bg-blue-50 border-blue-100">
                <CardContent className="p-4">
                  <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
                  <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
                    <li>Share the QR code or link with contractors</li>
                    <li>They'll fill out their company information</li>
                    <li>They'll upload their W-9 to complete registration</li>
                    <li>Once registered, they appear in your contractor list</li>
                  </ol>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="email" className="space-y-4">
              {/* Email Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="invite-email" className="text-sm font-medium text-slate-700">
                    Contractor's Email *
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="contractor@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="pl-10"
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="invite-message" className="text-sm font-medium text-slate-700">
                    Personal Message (optional)
                  </Label>
                  <textarea
                    id="invite-message"
                    placeholder="Hi! I'd like to invite you to join my contractor network..."
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    data-testid="invite-message-input"
                  />
                </div>
                
                <Button 
                  className="w-full gap-2" 
                  onClick={sendEmailInvite}
                  disabled={isSendingEmail || !inviteEmail}
                  data-testid="send-invite-btn"
                >
                  {isSendingEmail ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                  ) : emailSent ? (
                    <><CheckCircle2 className="w-4 h-4" /> Sent!</>
                  ) : (
                    <><Send className="w-4 h-4" /> Send Invite</>
                  )}
                </Button>
              </div>
              
              {/* Email Preview */}
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-slate-700 mb-2 text-sm">Email Preview</h4>
                  <div className="text-xs text-slate-600 space-y-2">
                    <p><strong>Subject:</strong> {companyName || 'A company'} invites you to join their contractor network</p>
                    <p><strong>From:</strong> DrawReady</p>
                    <div className="bg-white p-3 rounded border border-slate-200 mt-2">
                      <p className="mb-2">{inviteMessage || 'You\'ve been invited to join as a contractor.'}</p>
                      <p className="text-blue-600 underline break-all">{signupUrl}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Regenerate Button - shown on both tabs */}
            <div className="flex justify-between items-center pt-4 mt-4 border-t">
              <p className="text-xs text-slate-500">
                Code: <span className="font-mono font-medium">{signupCode}</span>
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={regenerateCode}
                disabled={isRegenerating}
                className="text-slate-600 hover:text-slate-900"
                data-testid="regenerate-code-btn"
              >
                {isRegenerating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Generate New Link
              </Button>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
